import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import Form, { Item } from 'devextreme-react/form';
import * as XLSX from 'xlsx';
import ArrayStore from 'devextreme/data/array_store';
import AntLocalization from '../../utils/AntLocalization';

export default forwardRef(function (props, ref) {
    const [fileName, setFileName] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);

    const onChoosing = () => {
        if (chooserForm.current) chooserForm.current.click();
    };

    const basedate = new Date(1899, 11, 30, 0, 0, 0);
    const dnthresh = basedate.getTime() + (new Date().getTimezoneOffset() - basedate.getTimezoneOffset()) * 60000;

    const day_ms = 24 * 60 * 60 * 1000;
    const days_1462_ms = 1462 * day_ms;

    const datenum = (v, date1904) => {
        let epoch = v.getTime();
        if (date1904) {
            epoch -= days_1462_ms;
        }
        return (epoch - dnthresh) / day_ms;
    };

    const parseDDMMYYYY = (str) => {
        if (typeof str !== 'string') return null;

        const parts = str.split('/');
        if (parts.length !== 3) return null;

        let [d, m, y] = parts.map((p) => parseInt(p, 10));

        // validate đơn giản
        if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
        if (m < 1 || m > 12 || d < 1 || d > 31) return null;

        return new Date(y, m - 1, d);
    };
    const parseDDMMYY = (str) => {
        if (typeof str !== 'string') return null;

        const parts = str.split('/');
        if (parts.length !== 3) return null;
        // Năm 4 chữ số để parseDDMMYYYY xử lý (tránh 2026 → 3926)
        if (parts[2].trim().length > 2) return null;

        let [d, m, y] = parts.map((p) => parseInt(p, 10));

        // validate đơn giản
        if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
        if (m < 1 || m > 12 || d < 1 || d > 31) return null;

        // chuyển 2 chữ số năm thành 4 chữ số
        if (y < 50) {
            y = 2000 + y;
        } else {
            y = 1900 + y;
        }

        return new Date(y, m - 1, d);
    };

    const fixImportedDate = (date, isDate1904) => {
        if (typeof date === 'string') {
            let parsedStr = parseDDMMYY(date);
            if (!parsedStr) parsedStr = parseDDMMYYYY(date);
            if (!parsedStr) return date; // throw new Error('Invalid date string: ' + date);
            date = parsedStr;
        }
        try {
            const parsed = XLSX.SSF.parse_date_code(datenum(date, isDate1904), { date1904: isDate1904 });

            return new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H, parsed.M, parsed.S);
        } catch (error) { }

        return date;
    };

    const getFileKind = (file) => {
        var name = (file && file.name) || '';
        var ext = String(name).split('.').pop().toLowerCase();
        if (['xlsx', 'xls', 'csv'].indexOf(ext) >= 0) return 'spreadsheet';
        if (['pdf'].indexOf(ext) >= 0) return 'pdf';
        if (['jpg', 'jpeg', 'png', 'webp', 'heic', 'bmp', 'gif'].indexOf(ext) >= 0) return 'image';
        if (['doc', 'docx'].indexOf(ext) >= 0) return 'document';
        return 'unknown';
    };

    const processFile = (file) => {
        if (!file) return;
        setFileName(file.name);

        var kind = getFileKind(file);

        // Non-spreadsheet → emit raw file để parent xử lý qua AI (nếu enabled)
        if (kind !== 'spreadsheet') {
            if (props.onChoosed) props.onChoosed({
                name: file.name,
                kind: kind,
                file: file,
                size: file.size,
                mime: file.type
            });
            return;
        }

        var reader = new FileReader();
        reader.onload = function (e) {
            var xlsxData = e.target.result;
            // CSV là văn bản thuần: giữ nguyên chuỗi (số điện thoại/mã có số 0 đầu), không để SheetJS tự đổi sang số
            const isCsv = /\.(csv|txt)$/i.test(String(file.name || ''));
            let wb = XLSX.read(xlsxData, { type: 'binary', cellDates: true, raw: isCsv });
            const ws = wb.Sheets[wb.SheetNames[0]];

            let isDate1904 = !!(wb.Workbook && wb.Workbook.WBProps && wb.Workbook.WBProps.date1904);

            var data = XLSX.utils.sheet_to_json(ws, { header: 1, cellDates: true });
            const headers = data.shift();
            var rows = [...data];
            rows = rows.filter((r) => {
                if (r instanceof Array === false) return false;

                var newR = r.filter((r2) => String(r2).trim() !== '');
                if (newR.length <= 0) return false;

                return true;
            });

            rows = rows.map((r) => {
                return r.map((r2) => {
                    // CSV đọc raw: số thuần (không có số 0 đầu) đổi lại thành số như file Excel; chuỗi có số 0 đầu (SĐT, mã) giữ nguyên
                    if (isCsv && typeof r2 === 'string' && /^-?(0|[1-9]\d{0,14})(\.\d+)?$/.test(r2.trim())) return Number(r2.trim());
                    if (r2 instanceof Date || (typeof r2 === 'string' && fixImportedDate(r2, isDate1904))) {
                        return fixImportedDate(r2, isDate1904);
                    }
                    if (typeof r2 === 'string') return String(r2).normalize();
                    return r2;
                });
            });

            const result = {
                name: file.name,
                kind: 'spreadsheet',
                file: file,
                size: file.size,
                mime: file.type,
                headers: headers,
                rows: rows,
                isDate1904: isDate1904
            };
            if (props.onChoosed) props.onChoosed(result);
        };
        reader.readAsBinaryString(file);
    };

    const onChoosed = (event) => {
        event.stopPropagation();
        event.preventDefault();
        var file = event.target.files[0];
        processFile(file);
        event.target.value = null;
    };

    const onDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isDragOver) setIsDragOver(true);
    };

    const onDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
    };

    const onDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        var file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (!file) return;
        processFile(file);
    };

    const onFieldDataChanged = (e) => {
        if (e.dataField === 'importMode') {
            if (props.onChangeMode) props.onChangeMode(e.value);
        }
    };

    const chooserForm = useRef();

    if (props.aiEnabled) {
        // Mode select inline (1 row) trước drop zone
        return (
            <React.Fragment>
                {props.modeChangable && !props.isCreateOnly && !props.isUpdateOnly && (
                    <Form
                        className="ant-form"
                        formData={{ importMode: props.importMode }}
                        colCount={1}
                        minColWidth={150}
                        onFieldDataChanged={onFieldDataChanged}
                        style={{ marginBottom: 8 }}
                    >
                        <Item
                            dataField="importMode"
                            editorType="dxSelectBox"
                            label={{ visible: false }}
                            editorOptions={{
                                placeholder: AntLocalization.translateText({ vi: 'Nghiệp vụ', en: 'Activity', fr: 'Activité', es: 'Actividad', id: 'Aktivitas', ja: '業務', zh: '业务', ko: '업무', hi: 'गतिविधि', ar: 'النشاط' }),
                                valueExpr: 'value',
                                displayExpr: 'name',
                                dataSource: new ArrayStore({
                                    key: 'value',
                                    data: [
                                        { name: AntLocalization.translateText({ vi: 'Tạo mới', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएं', ar: 'إنشاء' }), value: props.createable ? 'Create' : undefined },
                                        { name: AntLocalization.translateText({ vi: 'Cập nhật', en: 'Update', es: 'Actualizar', id: 'Perbarui', ja: '更新', zh: '更新', ko: '업데이트', hi: 'अपडेट करें', ar: 'تحديث', fr: 'Mise à jour' }), value: props.updateable ? 'Update' : undefined },
                                        {
                                            name: AntLocalization.translateText({ vi: 'Tạo mới và cập nhật', en: 'Create & update', fr: 'Créer et mettre à jour', es: 'Crear y actualizar', id: 'Buat & perbarui', ja: '作成と更新', zh: '创建并更新', ko: '생성 및 업데이트', hi: 'बनाएं और अपडेट करें', ar: 'إنشاء وتحديث' }),
                                            value: props.createable && props.updateable ? 'CreateUpdate' : undefined
                                        }
                                    ].filter((a) => Boolean(a.value))
                                }),
                                disabled: props.isCreateOnly || props.isUpdateOnly
                            }}
                        />
                    </Form>
                )}

                <div
                    onClick={onChoosing}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    style={{
                        position: 'relative',
                        border: `2px dashed ${isDragOver ? '#0ea5e9' : '#cbd5e1'}`,
                        background: isDragOver ? '#eff6ff' : fileName ? '#f0fdf4' : '#f8fafc',
                        borderRadius: 12,
                        padding: '28px 24px',
                        minHeight: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        transition: 'background 0.15s ease, border-color 0.15s ease',
                        textAlign: 'center'
                    }}
                >
                    <span
                        className={`dx-icon ${fileName ? 'dx-icon-doc' : 'dx-icon-upload'}`}
                        style={{ fontSize: 56, color: fileName ? '#15803d' : isDragOver ? '#0ea5e9' : '#94a3b8', lineHeight: 1 }}
                    />
                    <div style={{ fontSize: 16, fontWeight: 600, color: fileName ? '#15803d' : '#0f172a' }}>
                        {fileName
                            ? fileName
                            : isDragOver
                                ? AntLocalization.translateText({ vi: 'Thả file vào đây', en: 'Drop the file here', fr: 'Déposez le fichier ici', es: 'Suelte el archivo aquí', id: 'Lepas file di sini', ja: 'ここにドロップ', zh: '将文件拖放到此处', ko: '여기에 파일을 놓으세요', hi: 'फ़ाइल यहाँ छोड़ें', ar: 'أفلت الملف هنا' })
                                : AntLocalization.translateText({ vi: 'Kéo thả file vào đây', en: 'Drag & drop file here', fr: 'Glissez le fichier ici', es: 'Arrastre el archivo aquí', id: 'Seret & lepas file di sini', ja: 'ここにファイルをドラッグ', zh: '将文件拖放到此处', ko: '파일을 끌어다 놓으세요', hi: 'फ़ाइल यहाँ खींचकर छोड़ें', ar: 'اسحب الملف وأفلته هنا' })}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>
                        {fileName
                            ? AntLocalization.translateText({ vi: 'Click để chọn file khác', en: 'Click to choose another file', fr: 'Cliquez pour changer de fichier', es: 'Haga clic para elegir otro archivo', id: 'Klik untuk memilih file lain', ja: 'クリックして別のファイルを選択', zh: '点击选择其他文件', ko: '클릭하여 다른 파일 선택', hi: 'दूसरी फ़ाइल चुनने हेतु क्लिक करें', ar: 'انقر لاختيار ملف آخر' })
                            : AntLocalization.translateText({ vi: 'hoặc click để chọn từ máy', en: 'or click to browse from your device', fr: 'ou cliquez pour parcourir votre appareil', es: 'o haga clic para buscar en su equipo', id: 'atau klik untuk memilih dari perangkat', ja: 'またはクリックして端末から選択', zh: '或点击从设备中选择', ko: '또는 클릭하여 기기에서 선택', hi: 'या डिवाइस से चुनने हेतु क्लिक करें', ar: 'أو انقر للاختيار من جهازك' })}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, letterSpacing: 0.3 }}>
                        {AntLocalization.translateText({ vi: 'Hỗ trợ: Excel, CSV, PDF, ảnh (JPG/PNG)', en: 'Supports: Excel, CSV, PDF, image (JPG/PNG)', fr: 'Formats : Excel, CSV, PDF, image (JPG/PNG)', es: 'Admite: Excel, CSV, PDF, imagen (JPG/PNG)', id: 'Mendukung: Excel, CSV, PDF, gambar (JPG/PNG)', ja: '対応: Excel, CSV, PDF, 画像 (JPG/PNG)', zh: '支持: Excel, CSV, PDF, 图片 (JPG/PNG)', ko: '지원: Excel, CSV, PDF, 이미지 (JPG/PNG)', hi: 'समर्थित: Excel, CSV, PDF, चित्र (JPG/PNG)', ar: 'يدعم: Excel, CSV, PDF, صورة (JPG/PNG)' })}
                    </div>
                </div>

                <input
                    type="file"
                    ref={chooserForm}
                    style={{ display: 'none' }}
                    accept=".xlsx,.xls,.csv,.pdf,.jpg,.jpeg,.png,.webp,.heic,.bmp,.gif,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/*"
                    onChange={onChoosed}
                />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Form
                className="ant-form"
                formData={{
                    importMode: props.importMode,
                    fileName: fileName
                }}
                colCount={props.modeChangable && !props.isCreateOnly && !props.isUpdateOnly ? 4 : 1}
                minColWidth={150}
                onFieldDataChanged={onFieldDataChanged}
                style={{ marginBottom: 5 }}
            >
                {props.modeChangable && !props.isCreateOnly && !props.isUpdateOnly && (
                    <Item
                        dataField="importMode"
                        editorType="dxSelectBox"
                        colSpan={1}
                        label={{ visible: false }}
                        editorOptions={{
                            placeholder: AntLocalization.translateText({ vi: 'Nghiệp vụ', en: 'Activity', fr: 'Activité', es: 'Actividad', id: 'Aktivitas', ja: 'アクティビティ', zh: '活动', ko: '활동', hi: 'गतिविधि', ar: 'نشاط' }),

                            valueExpr: 'value',
                            displayExpr: 'name',
                            dataSource: new ArrayStore({
                                key: 'value',
                                data: [
                                    {
                                        name: AntLocalization.translateText({ vi: 'Tạo mới', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएं', ar: 'إنشاء' }),
                                        value: props.createable ? 'Create' : undefined
                                    },
                                    {
                                        name: AntLocalization.translateText({ vi: 'Cập nhật', en: 'Update', fr: 'Mettre à jour', es: 'Actualizar', id: 'Perbarui', ja: '更新', zh: '更新', ko: '업데이트', hi: 'अपडेट करें', ar: 'تحديث' }),
                                        value: props.updateable ? 'Update' : undefined
                                    },
                                    {
                                        name: AntLocalization.translateText({ vi: 'Tạo mới và cập nhật', en: 'Create & update', fr: 'Créer et mettre à jour', es: 'Crear y actualizar', id: 'Buat & perbarui', ja: '作成と更新', zh: '创建与更新', ko: '생성 및 업데이트', hi: 'बनाएं और अपडेट करें', ar: 'إنشاء وتحديث' }),
                                        value: props.createable && props.updateable ? 'CreateUpdate' : undefined
                                    }
                                ].filter((a) => Boolean(a.value))
                            }),
                            disabled: props.isCreateOnly || props.isUpdateOnly
                        }}
                    />
                )}
                <Item
                    dataField="fileName"
                    editorType="dxTextBox"
                    colSpan={3}
                    label={{ visible: false }}
                    editorOptions={{
                        placeholder: props.aiEnabled
                            ? AntLocalization.translateText({ vi: 'Chọn file (Excel, CSV, PDF, ảnh)...', en: 'Upload file (Excel, CSV, PDF, image)...', fr: 'Télécharger (Excel, CSV, PDF, image)...', es: 'Subir (Excel, CSV, PDF, imagen)...', id: 'Unggah (Excel, CSV, PDF, gambar)...', ja: 'アップロード (Excel, CSV, PDF, 画像)...', zh: '上传 (Excel, CSV, PDF, 图片)...', ko: '업로드 (Excel, CSV, PDF, 이미지)...', hi: 'अपलोड (Excel, CSV, PDF, छवि)...', ar: 'تحميل (Excel, CSV, PDF, صورة)...' })
                            : AntLocalization.translateText({ vi: 'Chọn file excel ...', en: 'Upload excel file ...', fr: 'Choisir fichier Excel ...', es: 'Subir archivo Excel ...', id: 'Unggah file Excel ...', ja: 'Excelファイルを選択 ...', zh: '上传Excel文件 ...', ko: 'Excel 파일 업로드 ...', hi: 'Excel फाइल अपलोड करें ...', ar: 'تحميل ملف Excel ...' }),

                        readOnly: true,
                        buttons: [
                            {
                                name: 'password',
                                location: 'after',
                                options: {
                                    icon: 'activefolder',
                                    type: 'normal',
                                    stylingMode: 'text',
                                    onClick: onChoosing,
                                    disabled: false
                                }
                            }
                        ]
                    }}
                />
            </Form>

            <input
                type="file"
                ref={chooserForm}
                style={{ display: 'none' }}
                accept={
                    props.aiEnabled
                        ? '.xlsx,.xls,.csv,.pdf,.jpg,.jpeg,.png,.webp,.heic,.bmp,.gif,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/*'
                        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,.xls,.csv'
                }
                onChange={onChoosed}
            />
        </React.Fragment>
    );
});
